import React from 'react';
import { Card, CardContent, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

const GlassCardContainer = styled(Card)(({ theme, variant = 'default' }) => {
  const baseStyles = {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'default',
    
    // Glassmorphism base
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.primary.main, 0.05)} 0%, 
        ${alpha(theme.palette.secondary.main, 0.03)} 50%, 
        ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
      zIndex: -1,
    },
    
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '1px',
      background: `linear-gradient(90deg, 
        transparent 0%, 
        ${alpha(theme.palette.primary.main, 0.3)} 50%, 
        transparent 100%)`,
      zIndex: 1,
    }
  };

  const variants = {
    default: {
      background: alpha(theme.palette.background.paper, 0.4),
      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
      
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      }
    },
    
    stats: {
      background: alpha(theme.palette.background.paper, 0.3),
      boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
      
      '&:hover': {
        transform: 'translateY(-2px) scale(1.02)',
        boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.2)}`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        
        '&::before': {
          background: `linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.08)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.05)} 50%, 
            ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
        }
      }
    },
    
    action: {
      background: alpha(theme.palette.background.paper, 0.2),
      boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.06)}`,
      cursor: 'pointer',
      
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.25)}`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
        
        '&::before': {
          background: `linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.1)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.06)} 50%, 
            ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        }
      },
      
      '&:active': {
        transform: 'translateY(-1px)',
      }
    },
    
    surface: {
      background: alpha(theme.palette.background.paper, 0.6),
      boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.05)}`,
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      
      '&:hover': {
        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
      }
    }
  };

  return {
    ...baseStyles,
    ...variants[variant]
  };
});

const GlassCard = ({ 
  children, 
  variant = 'default', 
  onClick, 
  sx = {}, 
  contentSx = {},
  ...props 
}) => {
  return (
    <GlassCardContainer 
      variant={variant} 
      onClick={onClick}
      sx={sx}
      {...props}
    >
      <CardContent sx={{ position: 'relative', zIndex: 2, ...contentSx }}>
        {children}
      </CardContent>
    </GlassCardContainer>
  );
};

export default GlassCard;
